import { create } from 'zustand';
import { notesService } from '../services/notesService';
import { notesApi } from '../lib/notesDb';
import type { Note } from '../lib/notesDb';
import type { VerseNote } from '../services/notesService';

interface NotesState {
  notes: Map<string, VerseNote>;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  // Actions
  addNote: (verseId: string, content: string) => Promise<void>;
  updateNote: (verseId: string, content: string) => Promise<void>;
  deleteNote: (verseId: string) => Promise<void>;
  getNote: (verseId: string) => Promise<VerseNote | null>;
  getAllNotes: () => Promise<VerseNote[]>;
  hasNotes: (verseId: string) => Promise<boolean>;
  getNotesForVerses: (verseIds: string[]) => Promise<VerseNote[]>;
  // Migration
  migrateFromIndexedDB: () => Promise<void>;
}

export const useNotesStore = create<NotesState>((set, get) => ({
  notes: new Map(),
  loading: false,
  error: null,
  initialized: false,

  addNote: async (verseId: string, content: string) => {
    set({ loading: true, error: null });
    try {
      const note = await notesService.addNote(verseId, content);
      set(state => ({
        notes: new Map(state.notes).set(verseId, note),
        loading: false
      }));
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
      throw error;
    }
  },

  updateNote: async (verseId: string, content: string) => {
    set({ loading: true, error: null });
    try {
      const note = await notesService.updateNote(verseId, content);
      set(state => ({
        notes: new Map(state.notes).set(verseId, note),
        loading: false
      }));
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
      throw error;
    }
  },

  deleteNote: async (verseId: string) => {
    set({ loading: true, error: null });
    try {
      await notesService.deleteNote(verseId);
      set(state => {
        const notes = new Map(state.notes);
        notes.delete(verseId);
        return { notes, loading: false };
      });
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
      throw error;
    }
  },

  getNote: async (verseId: string) => {
    set({ loading: true, error: null });
    try {
      const note = await notesService.getNote(verseId);
      if (note) {
        set(state => ({
          notes: new Map(state.notes).set(verseId, note),
          loading: false
        }));
      }
      return note;
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
      throw error;
    }
  },

  getAllNotes: async () => {
    set({ loading: true, error: null });
    try {
      const notes = await notesService.getAllNotes();
      const notesMap = new Map(notes.map(note => [note.verse_id, note]));
      set({ notes: notesMap, loading: false });
      return notes;
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
      throw error;
    }
  },

  hasNotes: async (verseId: string) => {
    try {
      return await notesService.hasNotes(verseId);
    } catch (error) {
      set({ error: (error as Error).message });
      return false;
    }
  },

  getNotesForVerses: async (verseIds: string[]) => {
    set({ loading: true, error: null });
    try {
      const notes = await notesService.getNotesForVerses(verseIds);
      const notesMap = new Map(notes.map(note => [note.verse_id, note]));
      set(state => ({
        notes: new Map([...state.notes, ...notesMap]),
        loading: false
      }));
      return notes;
    } catch (error) {
      // Set loading to false but don't set error for auth-related issues
      set({ loading: false });
      if (error instanceof Error && !error.message.includes('authenticated')) {
        set({ error: error.message });
      }
      return [];
    }
  },

  migrateFromIndexedDB: async () => {
    if (get().initialized) return;

    set({ loading: true, error: null });
    try {
      // Get all notes from IndexedDB
      const oldNotes = await notesApi.getAllNotes();
      
      // Migrate each note to Supabase
      for (const note of oldNotes) {
        await notesService.addNote(note.verseId, note.content);
      }

      // Clear IndexedDB after successful migration
      await notesApi.clearDatabase();
      
      set({ initialized: true, loading: false });
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
      throw error;
    }
  }
}));
